import { useEffect, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Button, Card, TextButton } from '@safetyhub/ui';
import { adminApi } from '../lib/api';
import { MEMBER_LABEL, OWNER_LABEL } from '../lib/roles';
import { businessMemberships } from '../lib/memberships';
import UserDetailModal from '../components/UserDetailModal';
import CreateUserDialog from '../components/CreateUserDialog';
import { useDebouncedValue } from '../hooks/useDebouncedValue';

type User = NonNullable<Awaited<ReturnType<typeof adminApi.getUsers>>['data']>[number];
type StatusFilter = 'all' | 'active' | 'inactive';

/** 한 쪽 행 수. 목록·상태·건수는 전부 서버가 정한다 — 받은 한 쪽 안에서 거르거나 세지 않는다. */
const USERS_PAGE_SIZE = 50;

export default function Users() {
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  // 검색은 서버 `search`로, 입력은 잠깐 멈춘 뒤 보낸다(업종 목록과 같은 형태).
  const [searchInput, setSearchInput] = useState('');
  const search = useDebouncedValue(searchInput.trim());
  const [page, setPage] = useState(0);

  // 조건이 바뀌면 첫 쪽으로.
  useEffect(() => {
    setPage(0);
  }, [search, statusFilter]);

  const status = statusFilter === 'active' ? 'active' : statusFilter === 'inactive' ? 'withdrawn' : undefined;

  // 키는 ['admin','users', …] 아래에 둔다 — 변경 화면들이 ['admin','users'] 접두로 무효화한다.
  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'users', 'list', { search, status, page }],
    queryFn: () =>
      adminApi.getUsers({
        limit: USERS_PAGE_SIZE,
        offset: page * USERS_PAGE_SIZE,
        search: search || undefined,
        status,
      }),
    placeholderData: keepPreviousData,
  });

  const filteredUsers = data?.data || [];
  // 상태 탭 건수 — 서버가 상태 필터를 뺀 같은 조건(검색)으로 센 전체 분모
  const counts = data?.meta?.counts;
  const statusCounts = {
    all: counts?.all ?? 0,
    active: counts?.active ?? 0,
    inactive: counts?.withdrawn ?? 0,
  };
  const total = data?.meta?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / USERS_PAGE_SIZE));

  // 탈퇴 처리 등으로 건수가 줄어 마지막 쪽이 사라지면 남아 있는 마지막 쪽으로 물린다.
  useEffect(() => {
    if (data && page > pageCount - 1) setPage(pageCount - 1);
  }, [data, page, pageCount]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">로딩 중...</div>
      </div>
    );
  }

  if (error || !data?.success) {
    return (
      <div className="bg-red-50 p-4 rounded text-red-600">
        데이터를 불러오는데 실패했습니다.
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-900">사용자 관리</h2>
        <div className="flex items-center gap-4">
          <input
            type="search"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="이름·이메일·전화·아이디 검색"
            aria-label="사용자 검색"
            className="w-64 px-3 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-brand-500"
          />
          <div className="text-sm text-gray-500">
            {search ? '검색 결과' : '총'} {statusCounts.all}명
            {statusCounts.inactive > 0 && (
              <span className="ml-2 text-orange-600">
                (탈퇴대기 {statusCounts.inactive}명)
              </span>
            )}
          </div>
          <Button variant="primary" onClick={() => setShowCreateDialog(true)}>
            + 사용자 생성
          </Button>
        </div>
      </div>

      {/* 상태 필터 탭 */}
      <div className="mb-4 border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setStatusFilter('all')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              statusFilter === 'all'
                ? 'border-brand-500 text-brand-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            전체
            <span className="ml-2 py-0.5 px-2 rounded-full text-xs bg-gray-100 text-gray-600">
              {statusCounts.all}
            </span>
          </button>
          <button
            onClick={() => setStatusFilter('active')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              statusFilter === 'active'
                ? 'border-brand-500 text-brand-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            활성
            <span className="ml-2 py-0.5 px-2 rounded-full text-xs bg-green-100 text-green-600">
              {statusCounts.active}
            </span>
          </button>
          <button
            onClick={() => setStatusFilter('inactive')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              statusFilter === 'inactive'
                ? 'border-brand-500 text-brand-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            탈퇴대기
            <span className="ml-2 py-0.5 px-2 rounded-full text-xs bg-orange-100 text-orange-600">
              {statusCounts.inactive}
            </span>
          </button>
        </nav>
      </div>
      {/* 탭 건수는 서버가 검색어를 적용해 센다 — 검색 중에는 전체 사용자 수가 아니다 (문구 임시) */}
      {search && (
        <p className="-mt-2 mb-3 text-xs text-gray-500">검색 결과 기준 건수입니다.</p>
      )}

      <Card padding="none" radius="lg" className="overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                사용자
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                역할
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                소속 조직
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                생성일
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                액션
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredUsers.map((user) => {
              // 로그인 식별자 표시 (우선순위: ID > EMAIL > KAKAO > GOOGLE)
              const idAuth = user.auths.find((a) => a.provider === 'ID');
              const emailAuth = user.auths.find((a) => a.provider === 'EMAIL');
              const kakaoAuth = user.auths.find((a) => a.provider === 'KAKAO');
              const googleAuth = user.auths.find((a) => a.provider === 'GOOGLE');

              let identifier = '-';
              if (idAuth) {
                identifier = idAuth.providerId;
              } else if (emailAuth) {
                identifier = emailAuth.providerId;
              } else if (kakaoAuth) {
                identifier = '카카오 로그인';
              } else if (googleAuth) {
                identifier = '구글 로그인';
              }

              const isWithdrawn = !!user.deletedAt;

              return (
                <tr
                  key={user.id}
                  className={`cursor-pointer ${
                    isWithdrawn
                      ? 'bg-orange-50 hover:bg-orange-100'
                      : 'hover:bg-gray-50'
                  }`}
                  onClick={() => setSelectedUser(user)}
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div
                        className={`w-10 h-10 rounded-full flex items-center justify-center font-medium ${
                          isWithdrawn
                            ? 'bg-orange-200 text-orange-600'
                            : 'bg-gray-200 text-gray-600'
                        }`}
                      >
                        {(user.member?.name ?? '?').charAt(0)}
                      </div>
                      <div className="ml-4">
                        <div className="flex items-center gap-2">
                          <span
                            className={`font-medium ${
                              isWithdrawn ? 'text-orange-700' : 'text-gray-900'
                            }`}
                          >
                            {user.member?.name ?? ''}
                          </span>
                          {isWithdrawn && (
                            user.deletionSource === 'ADMIN' ? (
                              <span className="px-1.5 py-0.5 text-xs bg-red-100 text-red-600 rounded">
                                강제탈퇴
                              </span>
                            ) : (
                              <span className="px-1.5 py-0.5 text-xs bg-orange-100 text-orange-600 rounded">
                                본인탈퇴
                              </span>
                            )
                          )}
                        </div>
                        <div className="text-sm text-gray-500">{identifier}</div>
                      </div>
                    </div>
                  </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {(() => {
                    // 비즈니스 조직에서의 종류(소유자/구성원)만 표시 (개인 작업공간·외부 연락처 제외).
                    // 술어는 `lib/memberships`가 정본 — 옛 `role.toUpperCase()`(2026-08-22 백지 사고)는 P7-b-2로 사라졌다.
                    const businessKinds = businessMemberships(user.memberships).map((m) =>
                      m.isOwner ? 'owner' : 'member',
                    );
                    const uniqueKinds = [...new Set(businessKinds)];

                    if (uniqueKinds.length === 0) {
                      return (
                        <span className="px-2 py-1 text-xs bg-gray-100 text-gray-500 rounded-full">
                          소속없음
                        </span>
                      );
                    }

                    return (
                      <div className="flex flex-wrap gap-1">
                        {uniqueKinds.map((kind) => (
                          <span
                            key={kind}
                            className={`px-2 py-1 text-xs rounded-full ${
                              kind === 'owner'
                                ? 'bg-purple-100 text-purple-600'
                                : 'bg-green-100 text-green-600'
                            }`}
                          >
                            {kind === 'owner' ? OWNER_LABEL : MEMBER_LABEL}
                          </span>
                        ))}
                      </div>
                    );
                  })()}
                </td>
                <td className="px-6 py-4">
                  {(() => {
                    // 같은 술어를 쓴다 — 역할 칸엔 안 뜨는데 소속 칸엔 뜨면 같은 개념이
                    // 두 칸에서 다르게 판정되는 셈이다(외부 연락처는 조직 구성원이 아니다).
                    const orgMemberships = businessMemberships(user.memberships);
                    return (
                      <div className="flex flex-wrap gap-1">
                        {orgMemberships.slice(0, 3).map((m, i) => (
                          <span
                            key={i}
                            className="px-2 py-1 text-xs bg-brand-50 text-brand-600 rounded"
                            title={m.organization.name}
                          >
                            {m.organization.name.length > 10
                              ? m.organization.name.substring(0, 10) + '...'
                              : m.organization.name}
                          </span>
                        ))}
                        {orgMemberships.length > 3 && (
                          <span className="px-2 py-1 text-xs bg-gray-100 text-gray-500 rounded">
                            +{orgMemberships.length - 3}
                          </span>
                        )}
                        {orgMemberships.length === 0 && (
                          <span className="text-xs text-gray-400">-</span>
                        )}
                      </div>
                    );
                  })()}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {new Date(user.createdAt).toLocaleDateString('ko-KR')}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <TextButton
                    className="text-sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedUser(user);
                    }}
                  >
                    상세보기
                  </TextButton>
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
        {filteredUsers.length === 0 && (
          <div className="px-6 py-10 text-center text-sm text-gray-500">
            {search || statusFilter !== 'all' ? '조건에 맞는 사용자가 없습니다.' : '사용자가 없습니다.'}
          </div>
        )}
      </Card>

      {total > 0 && (
        <div className="flex items-center justify-between mt-3 text-sm text-gray-600">
          <span>
            {total}명 중 {page * USERS_PAGE_SIZE + 1}–{page * USERS_PAGE_SIZE + filteredUsers.length}
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage((p) => p - 1)}
              disabled={page === 0}
              className="px-3 py-1 rounded border border-gray-300 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              이전
            </button>
            <span>
              {page + 1} / {pageCount}
            </span>
            <button
              onClick={() => setPage((p) => p + 1)}
              disabled={page >= pageCount - 1}
              className="px-3 py-1 rounded border border-gray-300 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              다음
            </button>
          </div>
        </div>
      )}

      <UserDetailModal
        user={selectedUser}
        isOpen={!!selectedUser}
        onClose={() => setSelectedUser(null)}
      />

      <CreateUserDialog
        isOpen={showCreateDialog}
        onClose={() => setShowCreateDialog(false)}
        onSuccess={() => {
          // 목록 새로고침은 CreateUserDialog 내부에서 처리됨
        }}
      />
    </div>
  );
}
